import { useState, useMemo, useEffect } from 'react';

interface UsePaginationProps<T> {
    data: T[];
    initialItemsPerPage?: number;
    initialPage?: number;
}

interface UsePaginationReturn<T> {
    // Datos paginados
    currentItems: T[];

    // Estado de paginación
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;

    // Funciones de control
    setCurrentPage: (page: number) => void;
    setItemsPerPage: (items: number) => void;
    goToFirstPage: () => void;
    goToLastPage: () => void;
    goToNextPage: () => void;
    goToPrevPage: () => void;

    // Información útil
    hasNextPage: boolean;
    hasPrevPage: boolean;
    startIndex: number;
    endIndex: number;
}

export const usePagination = <T,>({
    data,
    initialItemsPerPage = 25,
    initialPage = 1
}: UsePaginationProps<T>): UsePaginationReturn<T> => {
    const [currentPage, setCurrentPageState] = useState(initialPage);
    const [itemsPerPage, setItemsPerPageState] = useState(initialItemsPerPage);

    // Calcular valores derivados
    const totalItems = data.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);

    // Asegurar que la página actual sea válida
    const validCurrentPage = Math.min(Math.max(1, currentPage), totalPages || 1);

    // Calcular índices
    const startIndex = (validCurrentPage - 1) * itemsPerPage;
    const endIndex = Math.min(startIndex + itemsPerPage, totalItems);

    // Obtener elementos de la página actual
    const currentItems = useMemo(() => {
        return data.slice(startIndex, endIndex);
    }, [data, startIndex, endIndex]);

    // Funciones de navegación
    const setCurrentPage = (page: number) => {
        const validPage = Math.min(Math.max(1, page), totalPages || 1);
        setCurrentPageState(validPage);
    };

    const setItemsPerPage = (items: number) => {
        setItemsPerPageState(items);
        // Ajustar la página actual si es necesario
        const newTotalPages = Math.ceil(totalItems / items);
        if (currentPage > newTotalPages) {
            setCurrentPageState(newTotalPages || 1);
        }
    };

    const goToFirstPage = () => setCurrentPage(1);
    const goToLastPage = () => setCurrentPage(totalPages);
    const goToNextPage = () => setCurrentPage(validCurrentPage + 1);
    const goToPrevPage = () => setCurrentPage(validCurrentPage - 1);

    // Estados de navegación
    const hasNextPage = validCurrentPage < totalPages;
    const hasPrevPage = validCurrentPage > 1;

    return {
        // Datos
        currentItems,

        // Estado
        currentPage: validCurrentPage,
        totalPages,
        totalItems,
        itemsPerPage,

        // Funciones
        setCurrentPage,
        setItemsPerPage,
        goToFirstPage,
        goToLastPage,
        goToNextPage,
        goToPrevPage,

        // Información
        hasNextPage,
        hasPrevPage,
        startIndex,
        endIndex
    };
};

// Hook para combinar paginación con filtrado
export const usePaginationWithFilter = <T,>(
    data: T[],
    filterFn: (item: T, searchTerm: string) => boolean,
    searchTerm: string,
    initialItemsPerPage = 25
) => {
    // Filtrar datos primero
    const filteredData = useMemo(() => {
        if (!searchTerm.trim()) return data;
        return data.filter(item => filterFn(item, searchTerm.toLowerCase()));
    }, [data, filterFn, searchTerm]);

    // Aplicar paginación a los datos filtrados
    const pagination = usePagination({
        data: filteredData,
        initialItemsPerPage
    });

    // Resetear a la primera página cuando cambie el filtro
    useEffect(() => {
        pagination.setCurrentPage(1);
    }, [searchTerm]);

    return {
        ...pagination,
        filteredData,
        isFiltered: searchTerm.trim().length > 0
    };
};
